// src/pages/f1/F1IssuersExclusion/new.tsx

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  CheckboxField,
  DatePickerField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

// Zod schema for new F1IssuersExclusion
const FormSchema = z.object({
  issuer_id: z.coerce.number().optional().nullable(),
  issuer_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),

      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  exception: z.boolean().optional().nullable(),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export function F1IssuersExclusionNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client: Clients.F1_QUANTILIA,
    table_name: Tables.F1_ISSUERS_EXCLUSION,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Issuer Exclusion
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name="issuer_name"
                  label={t(
                    `${Tables.F1_ISSUERS_EXCLUSION}.columns.issuer_name`,
                    {
                      ns: "tables",
                      defaultValue: "Issuer",
                    },
                  )}
                  isRequired={true}
                  control={form.control}
                  base_url="f1-quantilia/f1_issuers_exclusion"
                  id_column="issuer_id"
                  column_name="issuer_name"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t(
                      `${Tables.F1_ISSUERS_EXCLUSION}.columns.issuer_name`,
                      {
                        ns: "tables",
                        defaultValue: "Date",
                      },
                    )}
                    isRequired={true}
                    control={form.control}
                  />
                  <CheckboxField
                    name="exception"
                    label={t(
                      `${Tables.F1_ISSUERS_EXCLUSION}.columns.exception`,
                      {
                        ns: "tables",
                        defaultValue: "Is Exception",
                      },
                    )}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating new item */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : t("no_changes")}
          </ConfirmationModal>

          {/* Cancel (navigate back) confirmation */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
